// src/encounter/services/use-encounter-answer-sets.ts
// The encounter's answer sets: `GET /api/questionnaire-responses/answer-sets?encounter=Encounter/{id}` → the heads (the
// current row) of the primary set and every inserted one, removed sets left out. The primary set is the note's own
// QuestionnaireResponse (`use-encounter-questionnaire-response.ts`); this read gives the templates added from the "/"
// menu, oldest first. A 404 (nothing there, or a server without the route) reads as no sets.
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { answerSetIdOf, byAuthoredAscending, isInsertedAnswerSet } from '../lib/answer-set';

export { answerSetIdOf, isInsertedAnswerSet };

export const encounterAnswerSetsKey = (encounterId: string | null) => ['encounter-answer-sets', encounterId] as const;

/** The inserted sets of a heads list, oldest first. */
export function insertedAnswerSets(heads: readonly QuestionnaireResponseResource[] | undefined): QuestionnaireResponseResource[] {
  return (heads ?? []).filter((h) => isInsertedAnswerSet(h) && !!answerSetIdOf(h)).sort(byAuthoredAscending);
}

/** The raw heads, for a caller that needs the cache entry itself. */
export async function fetchAnswerSetHeads(encounterId: string, signal?: AbortSignal): Promise<QuestionnaireResponseResource[]> {
  try {
    const heads = await get<QuestionnaireResponseResource[]>(
      `/api/questionnaire-responses/answer-sets?encounter=${encodeURIComponent(`Encounter/${encounterId}`)}`,
      { signal },
    );
    return Array.isArray(heads) ? heads : [];
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return [];
    throw e;
  }
}

export function useEncounterAnswerSets(encounterId: string | null) {
  return useQuery<QuestionnaireResponseResource[], Error, QuestionnaireResponseResource[]>({
    queryKey: encounterAnswerSetsKey(encounterId),
    enabled: !!encounterId,
    queryFn: ({ signal }) => fetchAnswerSetHeads(encounterId as string, signal),
    // The cache keeps the raw list (a mutation edits it in place); the hook hands back the inserted sets only.
    select: insertedAnswerSets,
  });
}
